/**
 * Campaign Repository
 * 
 * Extends BaseRepository with campaign-specific operations.
 * Common CRUD inherited from BaseRepository - no duplication!
 */

import { httpsCallable } from 'firebase/functions';
import { Campaign } from './Campaign';
import { BaseRepository } from './BaseRepository';

export class CampaignRepository extends BaseRepository {
  constructor(db, functions) {
    super(db, functions, 'discovery_campaigns', Campaign);
  }

  // ========================================
  // SPECIALIZED FINDER METHODS
  // ========================================

  /**
   * Find campaigns by status
   * @param {string} status - Status to filter by
   * @returns {Promise<Campaign[]>} - Array of Campaign instances
   */
  async findByStatus(status) {
    return this.findAll({
      where: [['status', '==', status]],
      orderBy: 'createdAt',
      orderDirection: 'desc'
    });
  }

  /**
   * Find all active campaigns (not archived)
   * @returns {Promise<Campaign[]>} - Array of Campaign instances
   */
  async findActive() {
    return this.findAll({
      where: [['status', '!=', 'archived']],
      orderBy: 'createdAt',
      orderDirection: 'desc'
    });
  }

  // ========================================
  // OVERRIDDEN METHODS
  // ========================================

  /**
   * Create a new campaign (override to add validation)
   * @param {Campaign} campaign - Campaign instance
   * @returns {Promise<string>} - Created campaign ID
   */
  async create(campaign) {
    // Validate before creating
    campaign.validate();
    
    const docData = campaign.toFirestore();
    const created = await super.create(docData); // Auto-generated ID
    
    return created.id;
  }

  /**
   * Delete a campaign (override to delegate to Cloud Function for cascade)
   * @param {string} id - Campaign ID
   * @returns {Promise<void>}
   */
  async delete(id) {
    // Load campaign to check if it can be deleted
    const campaign = await this.findById(id);
    
    if (!campaign) {
      throw new Error(`Campaign ${id} not found`);
    }
    
    // Check business rules
    if (!campaign.canBeDeleted()) {
      const reason = campaign.getDeleteBlockReason();
      throw new Error(`Cannot delete campaign: ${reason}`);
    }
    
    // Delegate to Cloud Function for cascade delete
    const deleteCampaignFn = httpsCallable(this.functions, 'deleteCampaign');
    await deleteCampaignFn({ campaignId: id });
  }

  // ========================================
  // CLOUD FUNCTION DELEGATIONS
  // ========================================

  /**
   * Recalculate campaign statistics (delegates to Cloud Function)
   * @param {string} campaignId - Campaign ID
   * @returns {Promise<void>}
   */
  async recalculateStats(campaignId) {
    const recalculateStatsFn = httpsCallable(this.functions, 'recalculateCampaignStats');
    await recalculateStatsFn({ campaignId });
  }

  /**
   * Archive a campaign (soft delete)
   * @param {string} campaignId - Campaign ID
   * @returns {Promise<void>}
   */
  async archive(campaignId) {
    await this.update(campaignId, {
      status: 'archived',
      archivedAt: new Date().toISOString()
    });
  }
}
